import React, { useState } from 'react';
import axios from 'axios';
import { useToast } from '../context/ToastContext';
import { useSettings } from '../context/SettingsContext';
import {
  FaMapMarkerAlt, FaEnvelope, FaPhoneAlt, FaClock, FaPaperPlane, FaCheckCircle,
} from 'react-icons/fa';
import './Contact.css';

const ADDRESS = '10 Akinwunmi Street, Alagomeji, Yaba, Lagos State, Nigeria';
const MAPS_QUERY = encodeURIComponent(ADDRESS);
const MAPS_EMBED = `https://www.google.com/maps?q=${MAPS_QUERY}&output=embed`;
const MAPS_LINK = `https://www.google.com/maps/search/?api=1&query=${MAPS_QUERY}`;

const Contact = () => {
  const toast = useToast();
  const { settings } = useSettings();
  const [formData, setFormData] = useState({
    name: '', email: '', phone: '', subject: '', message: '',
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post('/api/contact', formData);
      toast.success('Message sent. We\'ll be in touch soon.');
      setSubmitted(true);
      setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
    } catch (error) {
      toast.error(error.response?.data?.message || 'Could not send message');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="contact-page page">
      <div className="container">
        <div className="contact-hero">
          <span className="hero-eyebrow">Get in touch</span>
          <h1>We'd love to hear from you</h1>
          <p>
            Whether you're shopping for your first instrument, looking for studio gear, or have
            a question about an order, our team is here to help.
          </p>
        </div>

        <div className="contact-grid">
          {/* Form */}
          <div className="contact-form-wrapper">
            <div className="card contact-card">
              <h2>Send us a message</h2>
              <p className="form-subtitle">Fill out the form and we'll get back within 24 hours</p>

              {submitted ? (
                <div className="form-success">
                  <FaCheckCircle />
                  <h3>Thanks for reaching out</h3>
                  <p>We've received your message and will respond shortly.</p>
                  <button onClick={() => setSubmitted(false)} className="btn btn-secondary">
                    Send another message
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit}>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Full name *</label>
                      <input
                        type="text" name="name" required
                        value={formData.name} onChange={handleChange}
                        placeholder="Jane Doe"
                      />
                    </div>
                    <div className="form-group">
                      <label>Email *</label>
                      <input
                        type="email" name="email" required
                        value={formData.email} onChange={handleChange}
                        placeholder="you@example.com"
                      />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>Phone</label>
                      <input
                        type="tel" name="phone"
                        value={formData.phone} onChange={handleChange}
                        placeholder="+234 800 000 0000"
                      />
                    </div>
                    <div className="form-group">
                      <label>Subject</label>
                      <select name="subject" value={formData.subject} onChange={handleChange}>
                        <option value="">Select a topic</option>
                        <option value="General Inquiry">General inquiry</option>
                        <option value="Product Question">Product question</option>
                        <option value="Order Support">Order support</option>
                        <option value="Service & Repairs">Service &amp; repairs</option>
                        <option value="Wholesale">Wholesale &amp; partnerships</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>
                  </div>

                  <div className="form-group">
                    <label>Message *</label>
                    <textarea
                      name="message" required rows="6"
                      value={formData.message} onChange={handleChange}
                      placeholder="How can we help?"
                    />
                  </div>

                  <button type="submit" className="btn btn-primary btn-lg btn-block" disabled={loading}>
                    {loading ? 'Sending...' : <><FaPaperPlane /> Send message</>}
                  </button>
                </form>
              )}
            </div>
          </div>

          {/* Info */}
          <aside className="contact-info">
            <div className="info-card">
              <div className="info-icon"><FaMapMarkerAlt /></div>
              <h4>Visit our showroom</h4>
              <p>10, Akinwunmi Street<br />Alagomeji, Yaba<br />Lagos State, Nigeria</p>
              <a
                href={MAPS_LINK}
                target="_blank"
                rel="noopener noreferrer"
                className="info-link"
              >
                Get directions →
              </a>
            </div>

            {settings?.contactPhone && (
              <div className="info-card">
                <div className="info-icon"><FaPhoneAlt /></div>
                <h4>Call us</h4>
                <p><a href={`tel:${settings.contactPhone}`}>{settings.contactPhone}</a></p>
                <small>Mon – Sat · 9am to 6pm WAT</small>
              </div>
            )}

            {settings?.contactEmail && (
              <div className="info-card">
                <div className="info-icon"><FaEnvelope /></div>
                <h4>Email us</h4>
                <p><a href={`mailto:${settings.contactEmail}`}>{settings.contactEmail}</a></p>
                <small>We reply within 24 hours</small>
              </div>
            )}

            <div className="info-card">
              <div className="info-icon"><FaClock /></div>
              <h4>Opening hours</h4>
              <p>
                Monday – Friday: 9:00 – 18:00<br />
                Saturday: 10:00 – 16:00<br />
                Sunday: Closed
              </p>
            </div>
          </aside>
        </div>

        {/* Map */}
        <div className="map-section">
          <div className="map-header">
            <div>
              <h2>Find us on the map</h2>
              <p>Pop in for a hands-on demo of any instrument</p>
            </div>
            <a
              href={MAPS_LINK}
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-secondary"
            >
              <FaMapMarkerAlt /> Open in Google Maps
            </a>
          </div>
          <div className="map-frame">
            <iframe
              title="Centre Techniques Pianos location"
              src={MAPS_EMBED}
              loading="lazy"
              allowFullScreen=""
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contact;
